import * as Sentry from '@sentry/nuxt';
import { jwtDecode } from 'jwt-decode';
import { defineStore } from 'pinia';

import { pushToDataLayer } from '@skeleton/utils/dataLayer';

import type {
  IProfile,
  IAuthorizationResponse,
  IParsedToken,
  ITgLoginData,
  ITgUserCallbackData,
  IPlayerAvatar,
  ISocialConnections,
  ISocialCallbackData,
} from '@skeleton/core/types';
import { ModalName } from '@skeleton/consts/modals';

interface IProfileStoreState {
  refreshPromise: Promise<string> | null;
  loading: boolean;
  isLoggedIn: boolean;
  sessionId: string;
  referralId: string;
  resentVerifyEmail: boolean;
  profile: Maybe<IProfile>;
  avatars: Maybe<IPlayerAvatar[]>;
  socialAuthEmailError: boolean;
  tokenCookieKey: string;
  onlineSubscription: any;
  socialConnections: ISocialConnections;
  fingerprintVisitor: Promise<string> | null;
  telegram: {
    initData: IWebApp['initDataUnsafe'] | undefined;
    initDataRaw: string | undefined;
    platform: IWebApp['platform'] | undefined;
    botUrl: string | undefined;
  };
}

export const useProfileStore = defineStore('profileStore', {
  state: (): IProfileStoreState => ({
    loading: false,
    refreshPromise: null,
    isLoggedIn: false,
    sessionId: '',
    referralId: '',
    resentVerifyEmail: false,
    profile: undefined,
    avatars: [],
    socialConnections: {},
    socialAuthEmailError: false,
    tokenCookieKey: 'access_token',
    onlineSubscription: undefined,
    fingerprintVisitor: null,
    telegram: {
      initData: undefined,
      initDataRaw: undefined,
      platform: undefined,
      botUrl: undefined,
    },
  }),

  getters: {
    userNickname(state): string {
      // MEGA-UPDATE
      const userName = state.profile?.nickname || state.profile?.firstName || state.profile?.tgUserId;
      if (userName) return userName;
      if (state.profile?.email) {
        const { getNicknameFromEmail } = useProjectMethods();
        return getNicknameFromEmail(state.profile.email);
      }
      return 'Unknown';
    },
    isTelegramUser(): boolean {
      return !!this.telegram.initDataRaw || !!this.telegram.botUrl;
    },
    tgUserId(): number | undefined {
      return this.telegram.initData?.user?.id;
    },
    tgPlatform(): IWebApp['platform'] | undefined {
      return this.telegram.platform;
    },
    tgUserInfo(): IWebAppUser | undefined {
      return this.telegram.initData?.user;
    },
    initDataRaw(): string | undefined {
      return this.telegram.initDataRaw;
    },
  },

  actions: {
    setSessionToken(tokenValue: string): void {
      const { setToStorage } = useBrowserStorage();
      setToStorage(this.tokenCookieKey, tokenValue, { maxAge: 60 * 60 * 24 * 365, readonly: false });
    },

    setReferralId(referralId: string): void {
      this.referralId = referralId;
    },

    getSessionToken(): Maybe<string> {
      const { getFromStorage } = useBrowserStorage();
      return getFromStorage(this.tokenCookieKey);
    },

    isTokenExpired(): boolean {
      const token = this.getSessionToken();
      if (!token) return false;

      try {
        const currentSession: IParsedToken = jwtDecode(token);
        return currentSession.exp ? currentSession.exp - 10 < Date.now() / 1000 : false;
      } catch (e) {
        return false;
      }
    },

    getCurrentSession(): IParsedToken | null {
      const token = this.getSessionToken();
      if (!token) return null;

      try {
        return jwtDecode(token);
      } catch (error) {
        return null;
      }
    },

    encodeSessionChange(key: 'login' | 'logout'): string {
      const time = Date.now();
      return window.btoa(`${time}-${key}`);
    },

    async removeSession(): Promise<void> {
      const { setToStorage } = useBrowserStorage();
      this.profile = undefined;
      setToStorage(this.tokenCookieKey, '');
      this.isLoggedIn = false;
      sessionStorage.removeItem('asyncInvoiceId');
      localStorage.setItem('changeSession', this.encodeSessionChange('logout'));

      const { updateChat } = useFreshchatStore();
      updateChat();

      const { deleteReturnGame } = useLayoutStore();
      deleteReturnGame();

      this.finishProfileDependencies();
      const { reconnectSocket } = useWebSocket();
      await reconnectSocket();
    },

    async getRefreshRequest(): Promise<string> {
      const storageRefreshStatus = localStorage.getItem('refreshSession');

      if (storageRefreshStatus === 'loading') {
        const { awaitRefreshParallel } = useProjectMethods();
        const newToken = await awaitRefreshParallel();
        if (newToken) {
          this.setSessionToken(newToken);
          return newToken;
        } else {
          this.removeSession();
          return '';
        }
      }

      localStorage.setItem('refreshSession', 'loading');
      const { refreshToken } = useCoreAuthApi();

      try {
        const data = await refreshToken();
        this.setSessionToken(data.accessToken);
        localStorage.setItem('refreshSession', data.accessToken);
        return data.accessToken;
      } catch (e) {
        const { getSessionToken } = useProfileStore();
        const token = getSessionToken();
        Sentry.withScope(scope => {
          scope.setLevel('error');
          scope.setContext('Refresh token', {
            token,
          });
          scope.captureException(e);
        });
        if (this.isTelegramUser) {
          // Еще одна попытка залогиниться
          await this.loginFromTelegramApp('Catch composable, get refresh');
        } else {
          this.removeSession();
          localStorage.removeItem('refreshSession');
        }
        return '';
      } finally {
        this.refreshPromise = null;
      }
    },

    refreshToken(): string | Promise<string> {
      if (this.refreshPromise) return this.refreshPromise;
      this.refreshPromise = this.getRefreshRequest();
      return this.refreshPromise;
    },

    startProfileDependencies(): void {
      const { getFavoriteGames, subscribeBetsSocket, subscribeSpinsSocket } = useGamesStore();
      const { getPlayerBonuses, getPlayerFreeSpins, getPlayerCashback, getDepositBonuses } = useBonusStore();
      const { getPlayerActiveQuests, subscribeQuestsSocket } = useQuestsStore();
      const { getPlayerLoyalty, subscribeLoyaltySocket } = useLoyaltyStore();
      const { getPopoverNotifications, subscribeNotificationSocket } = useNotificationStore();
      const { goToLoadUserNotifications, subscribeUserNotificationSocket } = useUserNotificationsStore();
      const runtimeConfig = useRuntimeConfig();
      getFavoriteGames();
      if (runtimeConfig.public?.questsEnabled) getPlayerActiveQuests();
      if (runtimeConfig.public?.loyaltyEnabled) getPlayerLoyalty();
      getPopoverNotifications();
      getPlayerCashback();
      goToLoadUserNotifications();

      const route = useRoute();
      const bonusesRoute = route.name === 'profile-bonuses' || route.name === 'locale-profile-bonuses';
      if (!bonusesRoute) {
        getPlayerBonuses();
        getPlayerFreeSpins();
        getDepositBonuses();
      }

      const { subscribeAccountSocket, subscribeInvoicesSocket } = useWalletStore();
      const { subscribeBonusCodeSocket, subscribeBonusSocket, subscribeFreeSpinsSocket } = useBonusStore();

      subscribeAccountSocket();
      subscribeInvoicesSocket();
      subscribeBonusCodeSocket();
      subscribeBonusSocket();
      subscribeFreeSpinsSocket();
      subscribeBetsSocket();
      subscribeSpinsSocket();
      this.subscribeOnlineSocket();
      if (runtimeConfig.public?.questsEnabled) subscribeQuestsSocket();
      if (runtimeConfig.public?.loyaltyEnabled) subscribeLoyaltySocket();
      subscribeNotificationSocket();
      subscribeUserNotificationSocket();

      const { setEquivalentCurrency } = useGlobalStore();
      const storageEquivalentCurrency = localStorage.getItem('equivalentCurrency');
      if (storageEquivalentCurrency) setEquivalentCurrency(storageEquivalentCurrency);
    },

    finishProfileDependencies(): void {
      const bonusStore = useBonusStore();
      bonusStore.$reset();

      const { unsubscribeAccountSocket, unsubscribeInvoiceSocket } = useWalletStore();
      const { unsubscribeBonusCodeSocket, unsubscribeBonusSocket, unsubscribeFreeSpinsSocket } = useBonusStore();
      const { unsubscribeBetsSocket, unsubscribeSpinsSocket } = useGamesStore();
      const { unsubscribeQuestsSocket } = useQuestsStore();
      const { unsubscribeLoyaltySocket } = useLoyaltyStore();
      const { unsubscribeNotificationSocket } = useNotificationStore();
      const { unsubscribeUserNotificationSocket } = useUserNotificationsStore();
      unsubscribeAccountSocket();
      unsubscribeInvoiceSocket();
      unsubscribeBonusCodeSocket();
      unsubscribeBonusSocket();
      unsubscribeFreeSpinsSocket();
      unsubscribeBetsSocket();
      unsubscribeSpinsSocket();
      this.unsubscribeOnlineSocket();
      unsubscribeQuestsSocket();
      unsubscribeLoyaltySocket();
      unsubscribeNotificationSocket();
      unsubscribeUserNotificationSocket();
    },

    initPixel(pixelEventName?: 'regfin') {
      const { $pixel } = useNuxtApp();
      $pixel.init(pixelEventName);
    },

    async handleLogin(authResponse: IAuthorizationResponse): Promise<void> {
      this.setSessionToken(authResponse.accessToken);
      this.profile = authResponse.profile;

      await nextTick();
      const { getUserAccounts } = useWalletStore();
      await getUserAccounts();

      this.isLoggedIn = true;
      localStorage.setItem('changeSession', this.encodeSessionChange('login'));

      this.startProfileDependencies();
      const { reconnectSocket } = useWebSocket();
      await reconnectSocket();

      useEvent('accountChanged');
    },

    sendLoginToAnalytics() {
      if (!this.profile) return;

      pushToDataLayer('login', {
        email: this.profile.email,
        user_id: this.profile.id,
      });
    },

    async logIn(loginData: any): Promise<void> {
      const { submitLoginData } = useCoreAuthApi();

      const frogoFingerprint = await useFrogoFingerprintId();
      const fingerprint = (await this.fingerprintVisitor) || undefined;

      const submitResult = await submitLoginData({
        ...loginData,
        fingerprint,
        frogoFingerprint,
      });
      await this.handleLogin(submitResult);

      this.sendLoginToAnalytics();
      this.initPixel();
    },

    registrationSucceeded(): void {
      const { showAlert } = useLayoutStore();
      const { alertsData, defaultLocaleAlertsData } = useGlobalStore();
      const { closeModalSync } = useModalStore();

      showAlert(alertsData?.profile?.successRegistration || defaultLocaleAlertsData?.profile?.successRegistration);
      closeModalSync(ModalName.SIGN_IN);
      closeModalSync(ModalName.SIGN_UP);
    },

    async linkSocial(data: ISocialCallbackData): Promise<void> {
      const { submitSocialLinkData } = useCoreAuthApi();
      const fingerprint = (await this.fingerprintVisitor) || undefined;
      const affiliateTag = useCookie('affiliateTag');
      const globalData = useGlobalStore();
      await submitSocialLinkData({
        ...data,
        query: data.query,
        provider: data.connection,
        locale: globalData.currentLocale?.code,
        fingerprint,
        affiliateTag: affiliateTag.value || undefined,
      });

      const router = useRouter();
      const { localizePath } = useProjectMethods();
      await router.replace(localizePath('/profile'));
    },

    async loginSocial(data: ISocialCallbackData): Promise<void> {
      const { submitSocialLoginData } = useCoreAuthApi();
      const fingerprint = (await this.fingerprintVisitor) || undefined;
      const affiliateTag = useCookie('affiliateTag');
      const globalData = useGlobalStore();
      const submitResult = await submitSocialLoginData({
        ...data,
        query: data.query,
        provider: data.connection,
        locale: globalData.currentLocale?.code,
        fingerprint,
        affiliateTag: affiliateTag.value || undefined,
        redirect_domain: window?.location.origin,
      });
      await this.handleLogin(submitResult);

      this.initPixel();

      const appStateData: { backRoute: string } | undefined = data.query.state
        ? JSON.parse(data.query.state)
        : undefined;
      const router = useRouter();
      const { localizePath } = useProjectMethods();
      const backUrl = appStateData?.backRoute ? decodeURIComponent(appStateData.backRoute) : undefined;
      await router.replace(backUrl || localizePath('/'));

      if (submitResult.profile?.isNewlyRegistered) {
        useEvent('analyticsEvent', {
          event: 'registrationSuccess',
          regType: 'social',
        });
        useEvent('pixelEvent', 'regfin');
        setTimeout(this.registrationSucceeded, 1000);
      }
    },

    async unlinkSocial(socialType: string): Promise<void> {
      const { unlinkSocial } = useCoreAuthApi();

      try {
        await unlinkSocial(socialType);
      } catch (error: any) {
        Sentry.withScope(scope => {
          scope.setLevel('error');
          scope.setContext(`Error unlink social, socialType: ${socialType}`, {
            statusCode: error.response?.status,
          });
          scope.captureException(error);
        });
      }
    },

    async autoLogin(token: string): Promise<void> {
      const { submitAutologinData } = useCoreAuthApi();
      const fingerprint = (await this.fingerprintVisitor) || undefined;
      const submitResult = await submitAutologinData({
        token,
        fingerprint,
      });
      await this.handleLogin(submitResult);
      this.initPixel();
    },

    // MEGA-UPDATE
    async registration(registrationData: any, requireEmailVerification: boolean): Promise<void> {
      const { submitRegistrationData } = useCoreAuthApi();
      const fingerprint = (await this.fingerprintVisitor) || undefined;
      const frogoFingerprint = await useFrogoFingerprintId();
      const affiliateTag = useCookie('affiliateTag');
      const submitResult = await submitRegistrationData({
        ...registrationData,
        fingerprint,
        frogoFingerprint,
        affiliateTag: affiliateTag.value || undefined,
      });
      !requireEmailVerification && (await this.handleLogin(submitResult));
      useEvent('analyticsEvent', {
        event: 'registrationSuccess',
        regType: 'email',
      });
      this.initPixel('regfin');
    },

    async phoneRegistration(registrationData: any): Promise<void> {
      const { registerByPhone } = useCoreAuthApi();
      const fingerprint = (await this.fingerprintVisitor) || undefined;
      const affiliateTag = useCookie('affiliateTag');
      const submitResult = await registerByPhone({
        ...registrationData,
        fingerprint,
        affiliateTag: affiliateTag.value || undefined,
      });
      await this.handleLogin(submitResult);

      this.initPixel('regfin');

      useEvent('analyticsEvent', {
        event: 'registrationSuccess',
        regType: 'phone',
      });
    },

    async getProfileData(): Promise<void> {
      const { getProfile } = useCoreProfileApi();
      this.profile = await getProfile();
      this.isLoggedIn = true;
    },

    async getSocialConnections(): Promise<void> {
      const { getSocialConnections } = useCoreProfileApi();

      try {
        this.socialConnections = { ...(await getSocialConnections()) };
      } catch (error: any) {
        Sentry.withScope(scope => {
          scope.setLevel('error');
          scope.setContext(`Error getting socialConnections`, {
            statusCode: error.response?.status,
          });
          scope.captureException(error);
        });
      }
    },

    async getAvatarsData(): Promise<void> {
      const { getAvatars } = useCoreProfileApi();
      try {
        this.avatars = await getAvatars();
      } catch (error: any) {
        Sentry.withScope(scope => {
          scope.setLevel('error');
          scope.setContext(`Error getting avatars`, {
            statusCode: error.response?.status,
          });
          scope.captureException(error);
        });
      }
    },

    setProfileData(data: IProfile): void {
      this.profile = data;
    },

    async logOutUser(): Promise<void> {
      const { logOut } = useCoreAuthApi();

      this.loading = true;

      try {
        await logOut();
      } finally {
        await this.removeSession();

        const router = useRouter();
        const { localizePath } = useProjectMethods();
        await router.push(localizePath('/'));

        this.loading = false;
      }
    },

    async resendVerifyEmail(): Promise<void> {
      const { showAlert } = useLayoutStore();
      const { alertsData, defaultLocaleAlertsData } = useGlobalStore();
      const { resendVerifyEmail } = useCoreProfileApi();
      try {
        await resendVerifyEmail();
        showAlert(alertsData?.profile?.resentVerification || defaultLocaleAlertsData?.profile?.resentVerification);
      } catch {
        showAlert(alertsData?.global?.somethingWrong || defaultLocaleAlertsData?.global?.somethingWrong);
      } finally {
        this.resentVerifyEmail = true;
      }
    },

    subscribeOnlineSocket(): void {
      const { createSubscription } = useWebSocket();
      this.onlineSubscription = createSubscription('global:online');
    },

    unsubscribeOnlineSocket(): void {
      if (this.onlineSubscription) {
        this.onlineSubscription.unsubscribe();
        this.onlineSubscription.removeAllListeners();
      }
    },

    async loginFromTelegramApp(context?: string) {
      const route = useRoute();
      const { getFromStorage } = useBrowserStorage(true);
      const affiliateTag = getFromStorage('affiliateTag');
      let refId = route.query?.ref_id;

      try {
        const { submitTelegramLoginData } = useCoreAuthApi();

        if (!this.telegram.initDataRaw) return;

        const frogoFingerprint = await useFrogoFingerprintId();

        const params: ITgLoginData = {
          initDataRaw: this.telegram.initDataRaw,
          frogoFingerprint,
        };

        if (affiliateTag) {
          params.affiliateTag = affiliateTag;
        }

        refId = Array.isArray(refId) ? refId[0] : refId;

        if (refId) {
          params.refId = refId;
        }

        const data = await submitTelegramLoginData(params);
        await this.handleLogin(data);
        this.sendLoginToAnalytics();
        this.initPixel();
      } catch (error: any) {
        const { showAlert } = useLayoutStore();
        const { alertsData, defaultLocaleAlertsData } = useGlobalStore();

        Sentry.withScope(scope => {
          scope.setLevel('error');
          scope.setContext(`Error loginFromTelegramApp ${context}`, {
            statusCode: error.response?.status,
          });
          scope.captureException(error);
        });

        if (error.response?.status === 403) {
          showAlert(alertsData?.profile?.accountBlocked || defaultLocaleAlertsData?.profile?.accountBlocked);
        } else if (error.response?.status === 401) {
          showAlert(alertsData?.profile?.telegramAuthError || defaultLocaleAlertsData?.profile?.telegramAuthError);
        }
      }
    },

    async syncTelegramUser(authData: ITgUserCallbackData) {
      const { syncTgUser } = useCoreProfileApi();
      const { showAlert } = useLayoutStore();
      const { alertsData, defaultLocaleAlertsData } = useGlobalStore();

      try {
        await syncTgUser({
          user_id: authData.id,
          username: authData.username,
          language_code: this.profile?.language || 'en',
          photo_url: authData.photo_url,
          name: [authData.first_name, authData.last_name].filter(Boolean).join(' '),
        });
      } catch (error) {
        Sentry.withScope(scope => {
          scope.setLevel('error');
          scope.setContext('Telegram Sync Error', {
            playerId: this.profile?.id,
            userId: authData.id,
            username: authData.username,
          });
          scope.captureException(error);
        });

        showAlert(alertsData?.profile?.telegramAuthError || defaultLocaleAlertsData?.profile?.telegramAuthError);
      }
    },
  },
});
